import { useState, useCallback, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AdminNav } from '../components/AdminChrome';
import { formatDate } from '../lib/date';

export default function AdminHelp() {
    const [password, setPassword] = useState(sessionStorage.getItem('mimique_admin_token') || '');
    const [authed, setAuthed] = useState(!!sessionStorage.getItem('mimique_admin_token'));
    const [loading, setLoading] = useState(false);
    const [sections, setSections] = useState([]);
    // new section form
    const [newTitle, setNewTitle] = useState('');
    const [newBody, setNewBody] = useState('');
    const [newVisible, setNewVisible] = useState(true);
    const [newOrder, setNewOrder] = useState(0);
    const [newMediaUrl, setNewMediaUrl] = useState('');
    const [newMediaType, setNewMediaType] = useState('none');
    // inline editing
    const [editing, setEditing] = useState({});

    const base = process.env.REACT_APP_BACKEND_URL || '';

    const apiFetch = useCallback((path, opts = {}) => {
        return fetch(`${base}${path}`, {
            ...opts,
            headers: { 'Authorization': `Bearer ${password}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
        });
    }, [base, password]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/admin/help');
            if (res.status === 401) { toast.error('Wrong password'); setAuthed(false); return; }
            const data = await res.json();
            setSections(data.sections || []);
        } catch {
            toast.error('Failed to load');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => { if (authed) load(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await apiFetch('/api/admin/help');
        setLoading(false);
        if (res.status === 401) { toast.error('Wrong password'); return; }
        sessionStorage.setItem('mimique_admin_token', password);
        const data = await res.json();
        setSections(data.sections || []);
        setAuthed(true);
    };


    const handleAddSection = async (e) => {
        e.preventDefault();
        if (!newTitle.trim() || !newBody.trim()) return;
        const res = await apiFetch('/api/admin/help/sections', {
            method: 'POST',
            body: JSON.stringify({
                title: newTitle.trim(),
                body: newBody.trim(),
                visible: newVisible,
                order: Number(newOrder),
                media_url: newMediaUrl.trim() || null,
                media_type: newMediaType !== 'none' ? newMediaType : null,
            }),
        });
        if (!res.ok) { toast.error('Failed to add'); return; }
        const section = await res.json();
        setSections(prev => [...prev, section].sort((a, b) => a.order - b.order));
        setNewTitle(''); setNewBody(''); setNewOrder(sections.length);
        setNewMediaUrl(''); setNewMediaType('none');
        toast.success('Section added');
    };

    const startEdit = (section) => {
        setEditing(prev => ({
            ...prev,
            [section.id]: {
                title: section.title,
                body: section.body,
                order: section.order,
                mediaUrl: section.media_url || '',
                mediaType: section.media_type || 'none',
            },
        }));
    };

    const cancelEdit = (id) => {
        setEditing(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const handleSaveEdit = async (id) => {
        const { title, body, order, mediaUrl, mediaType } = editing[id];
        const res = await apiFetch(`/api/admin/help/sections/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({
                title,
                body,
                order: Number(order),
                media_url: mediaUrl?.trim() || null,
                media_type: mediaType !== 'none' ? mediaType : null,
            }),
        });
        if (!res.ok) { toast.error('Save failed'); return; }
        const updated = await res.json();
        setSections(prev => prev.map(s => s.id === id ? updated : s).sort((a, b) => a.order - b.order));
        cancelEdit(id);
        toast.success('Saved');
    };

    const handleToggle = async (id) => {
        const res = await apiFetch(`/api/admin/help/sections/${id}/toggle`, { method: 'POST' });
        if (!res.ok) { toast.error('Toggle failed'); return; }
        const updated = await res.json();
        setSections(prev => prev.map(s => s.id === id ? { ...s, visible: updated.visible } : s));
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this section?')) return;
        const res = await apiFetch(`/api/admin/help/sections/${id}`, { method: 'DELETE' });
        if (!res.ok) { toast.error('Delete failed'); return; }
        setSections(prev => prev.filter(s => s.id !== id));
        toast.success('Deleted');
    };

    if (!authed) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <form onSubmit={handleLogin} className="w-full max-w-xs space-y-4">
                    <h1 className="text-xl font-bold text-foreground">Admin — Help Page</h1>
                    <input
                        type="password"
                        placeholder="Admin password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                        autoFocus
                    />
                    <Button type="submit" className="w-full rounded-full" disabled={loading}>
                        {loading ? 'Checking…' : 'Log in'}
                    </Button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background px-4 py-10 max-w-3xl mx-auto space-y-10">
            <div><AdminNav /><h1 className="text-xl font-bold text-foreground">Help &amp; Guide</h1></div>

            {/* Add section */}
            <section className="space-y-3">
                <h2 className="font-semibold text-sm uppercase tracking-widest text-stone-400">Add Help Section</h2>
                <form onSubmit={handleAddSection} className="space-y-2">
                    <div className="flex gap-2 flex-wrap items-center">
                        <input
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                            placeholder="Title (e.g. How to practise)"
                            className="flex-1 min-w-48 px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <input
                            type="number"
                            value={newOrder}
                            onChange={e => setNewOrder(e.target.value)}
                            placeholder="Order"
                            className="w-20 px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50"
                        />
                        <label className="flex items-center gap-1.5 text-sm text-stone-600 cursor-pointer">
                            <input type="checkbox" checked={newVisible} onChange={e => setNewVisible(e.target.checked)} />
                            Visible
                        </label>
                    </div>
                    <textarea
                        value={newBody}
                        onChange={e => setNewBody(e.target.value)}
                        placeholder="Body text — describe this feature or tip. Line breaks are preserved."
                        rows={4}
                        className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50 resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {/* Optional media */}
                    <div className="flex gap-2 flex-wrap items-center">
                        <select
                            value={newMediaType}
                            onChange={e => setNewMediaType(e.target.value)}
                            className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50"
                        >
                            <option value="none">No media</option>
                            <option value="video">Video</option>
                            <option value="image">Image / Photo</option>
                        </select>
                        {newMediaType !== 'none' && (
                            <input
                                value={newMediaUrl}
                                onChange={e => setNewMediaUrl(e.target.value)}
                                placeholder={newMediaType === 'video' ? 'YouTube URL or direct video URL' : 'Image URL'}
                                className="flex-1 min-w-48 px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        )}
                    </div>
                    <div className="flex justify-end">
                        <Button type="submit" className="rounded-full" disabled={!newTitle.trim() || !newBody.trim()}>
                            Add section
                        </Button>
                    </div>
                </form>
            </section>

            {/* Sections list */}
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-sm uppercase tracking-widest text-stone-400">
                        Sections ({sections.length})
                    </h2>
                    <button onClick={load} className="text-xs text-stone-400 hover:text-stone-600 underline">Refresh</button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : sections.length === 0 ? (
                    <p className="text-stone-400 text-sm">No sections yet. Add one above.</p>
                ) : (
                    <div className="space-y-2">
                        {sections.map(section => {
                            const isEditing = !!editing[section.id];
                            const draft = editing[section.id] || {};

                            return (
                                <div key={section.id} className={`rounded-lg border ${section.visible ? 'border-stone-200' : 'border-stone-100 opacity-60'}`}>
                                    {/* Header row */}
                                    <div className="flex items-center gap-3 px-3 py-2">
                                        <span className="text-xs text-stone-400 w-6 shrink-0 text-center font-mono">{section.order}</span>
                                        <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">{section.title}</span>
                                        <span className="text-xs text-stone-300 shrink-0">{formatDate(section.created_at)}</span>
                                        {!isEditing && (
                                            <button onClick={() => startEdit(section)} className="text-xs text-stone-400 hover:text-stone-600 shrink-0">
                                                Edit
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleToggle(section.id)}
                                            className={`text-xs px-2 py-1 rounded-full border shrink-0 ${section.visible ? 'border-primary text-primary' : 'border-stone-300 text-stone-400'}`}
                                        >
                                            {section.visible ? 'Visible' : 'Hidden'}
                                        </button>
                                        <button onClick={() => handleDelete(section.id)} className="text-xs text-red-400 hover:text-red-600 shrink-0">
                                            Delete
                                        </button>
                                    </div>

                                    {/* Body preview (collapsed) */}
                                    {!isEditing && (
                                        <div className="px-3 pb-2 flex items-start gap-2">
                                            <p className="text-xs text-stone-400 line-clamp-2 whitespace-pre-line flex-1">{section.body}</p>
                                            {section.media_type && section.media_type !== 'none' && (
                                                <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${section.media_type === 'image' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
                                                    {section.media_type === 'image' ? '🖼 photo' : '▶ video'}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Inline edit form */}
                                    {isEditing && (
                                        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-stone-100">
                                            <div className="flex gap-2 items-center">
                                                <input
                                                    value={draft.title}
                                                    onChange={e => setEditing(prev => ({ ...prev, [section.id]: { ...prev[section.id], title: e.target.value } }))}
                                                    className="flex-1 px-2 py-1.5 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                                                    placeholder="Title"
                                                    autoFocus
                                                />
                                                <input
                                                    type="number"
                                                    value={draft.order}
                                                    onChange={e => setEditing(prev => ({ ...prev, [section.id]: { ...prev[section.id], order: e.target.value } }))}
                                                    className="w-20 px-2 py-1.5 border border-stone-200 rounded-lg text-sm bg-stone-50"
                                                    placeholder="Order"
                                                />
                                            </div>
                                            <textarea
                                                value={draft.body}
                                                onChange={e => setEditing(prev => ({ ...prev, [section.id]: { ...prev[section.id], body: e.target.value } }))}
                                                rows={4}
                                                className="w-full px-2 py-1.5 border border-stone-200 rounded-lg text-sm bg-stone-50 resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                                            />
                                            {/* Media */}
                                            <div className="flex gap-2 flex-wrap items-center">
                                                <select
                                                    value={draft.mediaType}
                                                    onChange={e => setEditing(prev => ({ ...prev, [section.id]: { ...prev[section.id], mediaType: e.target.value } }))}
                                                    className="px-2 py-1.5 border border-stone-200 rounded-lg text-sm bg-stone-50"
                                                >
                                                    <option value="none">No media</option>
                                                    <option value="video">Video</option>
                                                    <option value="image">Image / Photo</option>
                                                </select>
                                                {draft.mediaType !== 'none' && (
                                                    <input
                                                        value={draft.mediaUrl}
                                                        onChange={e => setEditing(prev => ({ ...prev, [section.id]: { ...prev[section.id], mediaUrl: e.target.value } }))}
                                                        placeholder={draft.mediaType === 'video' ? 'YouTube or video URL' : 'Image URL'}
                                                        className="flex-1 min-w-48 px-2 py-1.5 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                                                    />
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    onClick={() => handleSaveEdit(section.id)}
                                                    className="rounded-full h-8 text-xs px-3"
                                                    disabled={!draft.title?.trim() || !draft.body?.trim()}
                                                >
                                                    Save
                                                </Button>
                                                <button onClick={() => cancelEdit(section.id)} className="text-xs text-stone-400 hover:text-stone-600 px-1">
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
